<template>
    <div class="my-habits-chart-motivation"
         :style="containerStyle">

        <highcharts :options="chartOptions"></highcharts>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

    import Vue from 'vue'
    import moment from 'moment'
    import * as _ from 'lodash'

    /**
     * Component to display motivation chart for a wellness Habit
     */
    export default Vue.extend({
        props: {
            size: String, // 'sm' or 'lg'
            habit: Object
        },
        computed: {
            chartOptions () {
                const options = this.getBaseOptions()
                if (this.habit) {
                    this.addHabitOptions(options)
                }
                return options
            },
            containerStyle () {
                if (this.size == 'lg') {
                    return {
                        width: '100%'
                    }
                } else {
                    return {
                        width: '80px'
                    }
                }
            }
        },
        methods: {
            getBaseOptions () {
                return {
                    chart: {
                        type: 'scatter',
                        height: '100%',
                        showAxes: false,
                        backgroundColor: 'transparent',
                        plotBackgroundColor: 'transparent',
                        margin: (this.size == 'lg' ? 40 : 7),
                    },
                    title: null,
                    subtitle: {
                        text: this.size == 'lg' ? this.$i18n.gt('LABEL_MY_HABITS_DOT_LEGEND') : null,
                        y: 0,
                        style: {
                            fontSize: '13px',
                        }
                    },
                    credits: { enabled: false },
                    tooltip: { enabled: this.size == 'lg' },
                    legend: { enabled: false },

                    xAxis: [{
                        visible: this.size == 'lg',
                        gridLineWidth: 0,
                        lineColor: 'transparent',
                        tickColor: 'transparent',
                        min: 1,
                        max: 10,
                        labels: { enabled: false },
                        title: {
                            align: 'low',
                            x: -12,
                            y: -10,
                            text: `« ${this.$i18n.gt('LABEL_MY_HABITS_LESS_IMPORTANT')}`,
                            style: {
                                fontSize: '13px',
                            }
                        },
                    }, {
                        visible: this.size == 'lg',
                        linkedTo: 0,
                        gridLineWidth: 0,
                        lineColor: 'transparent',
                        tickColor: 'transparent',
                        min: 1,
                        max: 10,
                        labels: { enabled: false },
                        title: {
                            align: 'high',
                            x: 15,
                            y: -10,
                            text: `${this.$i18n.gt('LABEL_MY_HABITS_MORE_IMPORTANT')} »`,
                            style: {
                                fontSize: '13px',
                            }
                        },
                        offset: 0
                    }],

                    yAxis: [{
                        visible: this.size == 'lg',
                        gridLineWidth: 0,
                        min: 1,
                        max: 10,
                        labels: { enabled: false },
                        title: {
                            align: 'low',
                            x: -18,
                            y: -20,
                            text: `« ${this.$i18n.gt('LABEL_MY_HABITS_LESS_CONFIDENT')}`,
                            style: {
                                fontSize: '13px',
                            }
                        },
                    }, {
                        visible: this.size == 'lg',
                        linkedTo: 0,
                        gridLineWidth: 0,
                        min: 1,
                        max: 10,
                        labels: { enabled: false },
                        title: {
                            align: 'high',
                            x: -18,
                            y: -20,
                            text: `${this.$i18n.gt('LABEL_MY_HABITS_MORE_CONFIDENT')} »`,
                            style: {
                                fontSize: '13px',
                            }
                        },
                        offset: 0,
                    },],

                    series: [
                        { type: 'scatter' }
                    ]
                }
            },
            addHabitOptions (options) {
                const IMPORTANCE = this.$i18n.gt('LABEL_MY_HABITS_IMPORTANCE')
                const CONFIDENCE = this.$i18n.gt('LABEL_MY_HABITS_CONFIDENCE')

                // add a separate data series for each motivation entry,
                // so we can color each marker differently with a "shadow trail" effect
                _.forEach(this.habit.motivationEntries, (entry, i) => {
                    // set alpha to 1 for last entry; step down by .3 for each previous entry
                    const alpha = 1 - 3 * (this.habit.motivationEntries.length - i - 1) / 10
                    // console.log(alpha)
                    options.series[i + 1] = {
                        type: 'scatter',
                        name: moment(entry.timestamp).format('M/D/YYYY'),
                        // TODO: change base color?
                        color: `rgb(128,128,128, ${alpha})`,
                        marker: {
                            symbol: 'circle',
                            radius: (this.size == 'lg' ? 12 : 4),
                        },
                        tooltip: {
                            headerFormat: '<span style="font-size: 10px">{series.name}</span><br/>',
                            pointFormatter: function () {
                                return `${IMPORTANCE}: ${this.x}/10, ${CONFIDENCE}: ${this.y}/10`
                            }
                        },
                        data: [[entry.importance, entry.confidence]]
                    }
                })
            }
        },
    })

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

    .my-habits-chart-motivation {
        margin: 0;
        padding: 0;

        // TODO: tweak bg image to fix alignment (7/7 rating should appear at bottom left corner of green box)
        background-image: url(/../../images/habits-motivation-bg.png);
        background-size: 100% 108%;
    }
</style>
